import { WaypointPosition } from "@/components/r3f/renderers/waypoint-label-render";
import { useMapPlaceholderPositions } from "@/hooks/use-map-placeholder-positions";
import { useAppSelector } from "@/store/store-hooks";
import { selectObjectVisibility } from "@/store/view-options/view-options-selectors";
import { ViewObjectTypes } from "@/store/view-options/view-options-slice";
import {
  IMapPlaceholderVariants,
  MapWaypointsRenderer,
  MapWaypointsRendererProps,
  MapWaypointsRendererRef,
  selectChildrenDepthFirst,
  TransformOverrides,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericImgSheet,
  isIElementImg360,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { useForkRef } from "@mui/material";
import { isEqual } from "es-toolkit";
import { forwardRef, useCallback, useMemo, useState, type JSX } from "react";
import { Plane, Vector3 } from "three";
import {
  IElementWaypoint,
  useOffsetPlaceholders,
  useVisiblePlaceholders,
  useWaypoints,
} from "../../hooks/use-placeholders";
import { SceneContextMenuData } from "./scene-context-menu/use-scene-context-menu";
import { SheetModeRenderOrders } from "./sheet-mode-render-orders";
import { SheetWaypointsTooltipLabel } from "./sheet-waypoints-tooltip-label";
import { useApplyTexturesToLabelWaypoints } from "./use-apply-textures-to-label-waypoints";
import { usePlaceholderHoverTooltip } from "./use-placeholder-hover-tooltip";
import { useSheetWaypointsColors } from "./use-sheet-waypoints-colors";
import { useActiveWaypointIndex } from "./use-waypoints-textures";

export type SheetWaypointsProps = {
  /** The list of paths to render */
  paths: IElementGenericOdometryPath[];

  /** The list of waypoints currently shown */
  placeholders: IElementWaypoint[];

  /** Optional element transform overrides to apply to the rendered elements. */
  transformOverrides?: TransformOverrides;

  /** Sheet whose elevation is used for the waypoints */
  sheetForElevation?: IElementGenericImgSheet;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** Callback issued when a placeholder is clicked */
  onPlaceholderClicked?(el: IElementWaypoint, event: MouseEvent): void;

  /** Callback issued when a context menu is requested for a placeholder */
  onPlaceholderContextMenu?(
    contextMenuData: SceneContextMenuData,
    event: MouseEvent,
  ): void;

  /** Callback issued when a placeholder is hovered */
  onPlaceholderHovered?(el: IElementWaypoint | undefined): void;

  /** Callback issued when a placeholder is moved to a new position */
  onPlaceholderMoved?(el: IElementWaypoint, newPosition: Vector3): void;

  /** Whether the moving of waypoints should be allowed */
  enableMoving?: boolean;

  /**
   * Function to compute on demand, whether a placeholder is allowed to be moved.
   * Should either return false, or a string with a reason why the placeholder cannot be moved.
   * If not provided, all placeholders are allowed to be moved.
   */
  isPlaceholderDisabledForMove?(el: IElementWaypoint): string | false;

  /**
   * Function to compute on demand, whether a placeholder is allowed to be clicked.
   * If not provided, all placeholders are allowed to be clicked.
   */
  isPlaceholderDisabledForClick?(el: IElementWaypoint): boolean;

  /** Custom textures used for the flash scans */
  flashTextures?: Partial<IMapPlaceholderVariants>;

  /** True if it is in minimap */
  isInMinimap?: boolean;
} & Pick<MapWaypointsRendererProps, "textures">;

/** @returns a component to instantiate the rendering of pano waypoints in sheet mode */
export const SheetWaypoints = forwardRef<
  MapWaypointsRendererRef,
  SheetWaypointsProps
>(function SheetWaypoints(
  {
    paths,
    placeholders,
    transformOverrides,
    sheetForElevation,
    clippingPlanes,
    onPlaceholderHovered,
    onPlaceholderClicked,
    onPlaceholderContextMenu,
    onPlaceholderMoved,
    enableMoving,
    isPlaceholderDisabledForMove,
    isPlaceholderDisabledForClick,
    textures,
    flashTextures,
    isInMinimap,
  }: SheetWaypointsProps,
  externalRef,
): JSX.Element {
  const positions = useMapPlaceholderPositions(
    placeholders,
    sheetForElevation,
    undefined,
    transformOverrides,
  );

  const panoOffset = usePanoOffset(paths, sheetForElevation);

  const { visiblePlaceholders, visiblePositions } = useVisiblePlaceholders({
    placeholders,
    positions,
    clippingPlanes,
  });

  const { activeWaypoint, activeWaypointIndex, activeLocationMarkerIndex } =
    useActiveWaypointIndex(visiblePlaceholders);

  const colors = useSheetWaypointsColors(
    visiblePlaceholders,
    enableMoving,
    isInMinimap,
    activeWaypoint,
    isPlaceholderDisabledForMove,
    isPlaceholderDisabledForClick,
  );

  const { placeholdersOffset, shiftedPlaceholders } =
    useOffsetPlaceholders(visiblePositions);

  const waypoints = useWaypoints(
    visiblePlaceholders,
    visiblePositions,
    panoOffset,
  );

  const [hoveredWaypoint, setHoveredWaypoint] = useState<WaypointPosition>();
  const onWaypointHovered = useCallback(
    (waypointIndex?: number) => {
      setHoveredWaypoint(
        waypointIndex === undefined ? undefined : waypoints[waypointIndex],
      );
      onPlaceholderHovered?.(
        waypointIndex === undefined
          ? undefined
          : waypoints[waypointIndex].placeHolder,
      );
    },
    [onPlaceholderHovered, waypoints],
  );

  const shouldWayPointsBeVisible = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.waypoints),
  );

  const [mapPlaceholders, setMapPlaceholders] =
    useState<MapWaypointsRendererRef | null>();

  const ref = useForkRef(setMapPlaceholders, externalRef);

  // This hook ensures that at all time the waypoints are rendered with the texture matching their type
  useApplyTexturesToLabelWaypoints(
    mapPlaceholders,
    shiftedPlaceholders,
    visiblePlaceholders,
    flashTextures,
  );

  const shouldDisplayWaypointLabels =
    useAppSelector(selectObjectVisibility(ViewObjectTypes.waypointLabels)) ||
    enableMoving;

  const onWaypointClicked = useCallback(
    (index: number, event: MouseEvent) => {
      if (isPlaceholderDisabledForClick?.(waypoints[index].placeHolder)) return;
      onPlaceholderClicked?.(waypoints[index].placeHolder, event);
    },
    [isPlaceholderDisabledForClick, waypoints, onPlaceholderClicked],
  );

  const onWaypointContextMenu = useCallback(
    (index: number, event: MouseEvent) => {
      if (isPlaceholderDisabledForClick?.(waypoints[index].placeHolder)) return;
      onPlaceholderContextMenu?.(
        { target: waypoints[index].placeHolder },
        event,
      );
    },
    [isPlaceholderDisabledForClick, waypoints, onPlaceholderContextMenu],
  );

  const onWaypointMoved = useCallback(
    (index: number, newPosition: Vector3) => {
      onPlaceholderMoved?.(placeholders[index], newPosition);
    },
    [onPlaceholderMoved, placeholders],
  );

  const isWaypointAllowedToMove = useCallback(
    (index: number) => !isPlaceholderDisabledForMove?.(placeholders[index]),
    [isPlaceholderDisabledForMove, placeholders],
  );

  const hoverTooltip = usePlaceholderHoverTooltip({
    waypoint: hoveredWaypoint?.placeHolder,
    enableMoving,
    isPlaceholderDisabledForMove,
  });

  // While moving and in the minimap, we want to highlight the active marker.
  // Otherwise, only location markers should be highlighted.
  const selectedId =
    enableMoving && isInMinimap
      ? activeWaypointIndex
      : activeLocationMarkerIndex;

  const groupName = useMemo(
    () => (isInMinimap ? "minimap-waypoints" : "sheet-waypoints"),
    [isInMinimap],
  );

  return (
    <>
      <group position={placeholdersOffset} name={groupName}>
        <MapWaypointsRenderer
          ref={ref}
          waypoints={shiftedPlaceholders}
          visible={shouldWayPointsBeVisible}
          customColors={colors}
          textures={textures}
          renderOrder={SheetModeRenderOrders.Waypoints}
          onPlaceholderClick={onWaypointClicked}
          onPlaceholderContextMenu={onWaypointContextMenu}
          onPlaceholderHovered={onWaypointHovered}
          onPlaceholderMoved={onWaypointMoved}
          enablePlaceholderMove={enableMoving}
          isPlaceholderAllowedToMove={isWaypointAllowedToMove}
          selectedId={selectedId}
          generateMipmaps
          channelBlending
        />
      </group>

      <SheetWaypointsTooltipLabel
        hoveredWaypoint={hoveredWaypoint}
        hoverTooltip={hoverTooltip}
        transformOverrides={transformOverrides}
        shouldDisplayWaypointLabels={shouldDisplayWaypointLabels}
        isInMinimap={isInMinimap}
        shouldWayPointsBeVisible={shouldWayPointsBeVisible}
        onPlaceholderClicked={onPlaceholderClicked}
        waypoints={waypoints}
      />
    </>
  );
});

/**
 * @param paths The trajectories in the sheet
 * @param sheetForElevation The sheet whose elevation is used to display the path
 * @returns the offset to apply to the pano placeholders to ensure they are rendered above the trajectory panos
 */
function usePanoOffset(
  paths: IElementGenericOdometryPath[],
  sheetForElevation?: IElementGenericImgSheet,
): number {
  const pathPanos = useAppSelector(
    (state) =>
      paths.flatMap((path) =>
        selectChildrenDepthFirst(path, isIElementImg360)(state),
      ),
    isEqual,
  );

  // TODO: OdometryPath are not included in this computation, we should
  // check if they need to be taken into account when computing the panos position
  // https://faro01.atlassian.net/browse/SWEB-8831
  const pathPanoPositions = useMapPlaceholderPositions(
    pathPanos,
    sheetForElevation,
  );

  return useMemo(() => {
    // To avoid z-fighting and to account for additional sheet offset of the trajectory panos
    const additionalOffset = 1;
    return (
      Math.max(0, ...pathPanoPositions.map((pos) => pos.y)) + additionalOffset
    );
  }, [pathPanoPositions]);
}
